import { useQuery } from '@tanstack/react-query';
import { Cesium3DTileStyle, Cesium3DTileset, defined } from 'cesium';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { DataUploadType, SPLAT_TYPE } from '@/common/data-upload';
import type { LayerTileset } from '@/helper/clipping-box-geometry';
import { sidecarUrlFor } from '@/helper/ifc/sidecar';
import { useAssetSettings } from '@/hooks/use-asset-settings';
// Module-level translate, not the hook: `t` must stay out of the `instantiate` and
// load-effect deps, or a language switch would re-run the whole tileset load
// (clipping box reset, repeated toasts, duplicate tilesets while loading).
import { t as translate } from '@/i18n';
import { getListDataUploads } from '@/queries/data-uploads/list-data-uploads';
import { ROLE } from '../../common/role';
import { type DeferredLayerInfo, useCesium } from '../../context/CesiumContext';
import { useIfcFeatures } from '../../context/IfcFeatureContext';
import { usePermission } from '../../hooks/use-permission';

type TilesLoaderProps = {
	onTilesetsLoaded?: (tilesets: Cesium3DTileset[]) => void;
};

// Everything needed to build a tileset later, so a layer the user switched off can
// stay in the layer list without its tileset.json ever being fetched.
type IonSpec = {
	kind: 'ion';
	uniqueId: string;
	name: string;
	cesiumId: number;
	subSurface?: boolean;
};

type UrlSpec = {
	kind: 'url';
	uniqueId: string;
	name: string;
	url: string;
	version: string;
	/**
	 * Conversion pipeline (`3d-model` | `cad` | `bim` | `gaussian-splat`), from the
	 * tileset record.
	 */
	tilesetType: string | null;
	// Gaussian splats need translucent depth picking to be grabbed (see helper/translucentDepth),
	// and they ignore clipping planes, so the limit box lists them as not clippable.
	splat: boolean;
};

type TilesetSpec = IonSpec | UrlSpec;

/**
 * Identifies the content a tileset was built from. A layer whose signature is unchanged
 * can stay in the scene across a reload of the upload list instead of being rebuilt —
 * saving a new location for one model must not re-download all the others.
 */
const specSignature = (spec: TilesetSpec) =>
	spec.kind === 'ion' ? `ion:${spec.cesiumId}` : `url:${spec.url}|${spec.version}`;

const TilesLoader: React.FC<TilesLoaderProps> = ({ onTilesetsLoaded }) => {
	const {
		viewer,
		setTilesLoaded,
		registerSubSurfaceLayer,
		undergroundEnabled,
		registerDeferredLayer,
		pendingLayerLoads,
		resolveLayerLoad,
	} = useCesium();
	const { registerIfcTileset, unregisterIfcTileset } = useIfcFeatures();
	const { assetId } = useParams<{ assetId: string }>();
	const { getLayerVisibility, updateSetting } = useAssetSettings(assetId);
	const shouldReadDataUploads = usePermission(ROLE.DATA_UPLOADS_READ);

	const registeredIds = useRef<string[]>([]);
	const deferredIds = useRef<string[]>([]);
	// Specs for every layer of this asset, loaded or not, keyed by uniqueId
	const specs = useRef<Map<string, TilesetSpec>>(new Map());
	const loadedTilesets = useRef<Cesium3DTileset[]>([]);
	const inFlight = useRef<Set<string>>(new Set());

	const onTilesetsLoadedRef = useRef(onTilesetsLoaded);
	onTilesetsLoadedRef.current = onTilesetsLoaded;

	const { data: uploadsData, isLoading } = useQuery({
		queryKey: ['data-uploads', { assetId, fileType: DataUploadType.CESIUM_ID }],
		queryFn: () =>
			getListDataUploads({
				assetId: assetId!,
				fileType: DataUploadType.CESIUM_ID,
				limit: 30,
				offset: 0,
			}),
		enabled: !!assetId && shouldReadDataUploads,
	});

	// Tile server (3D Tiles) uploads — rendered from their tileset.json URL.
	// Filter by uploadType so both GLB and gaussian-splat (.ply) tilesets load.
	const { data: tilesetUploadsData, isLoading: isLoadingTilesets } = useQuery({
		queryKey: ['data-uploads', { assetId, uploadType: DataUploadType.TILESET }],
		queryFn: () =>
			getListDataUploads({
				assetId: assetId!,
				uploadType: DataUploadType.TILESET,
				limit: 30,
				offset: 0,
			}),
		enabled: !!assetId && shouldReadDataUploads,
	});

	const tilesetsToLoad = useMemo<IonSpec[]>(() => {
		if (!uploadsData?.dataUploads) return [];
		return uploadsData.dataUploads
			.filter(
				(upload) =>
					upload.uploadType === DataUploadType.CESIUM_ID && Number.isFinite(Number(upload.cesiumId))
			)
			.map((upload) => ({
				kind: 'ion' as const,
				uniqueId: `asset-${Number(upload.cesiumId)}`,
				cesiumId: Number(upload.cesiumId),
				name: upload.name ?? '',
				subSurface: upload.subSurfaceMode ?? false,
			}));
	}, [uploadsData]);

	// Only ready tilesets that have a tileset.json URL can be rendered
	const urlTilesetsToLoad = useMemo<UrlSpec[]>(() => {
		if (!tilesetUploadsData?.dataUploads) return [];
		return tilesetUploadsData.dataUploads
			.filter(
				(upload) =>
					upload.uploadType === DataUploadType.TILESET &&
					upload.tileset?.status === 'ready' &&
					!!upload.tileset?.url
			)
			.map((upload) => ({
				kind: 'url' as const,
				uniqueId: `tileset-${upload.id}`,
				url: upload.tileset?.url as string,
				name: upload.name ?? '',
				// Version the URL by the tileset's updatedAt: stable across normal loads
				// (CloudFront HIT) but changes after a reposition (cache-key miss -> fresh).
				version: upload.tileset?.updatedAt ?? '',
				tilesetType: upload.tileset?.type ?? null,
				splat: upload.tileset?.type === SPLAT_TYPE,
			}));
	}, [tilesetUploadsData]);

	// Builds the tileset and puts it in the scene. This is the only place that hits
	// the network, so it must not run for a layer the user has switched off.
	const instantiate = useCallback(
		async (spec: TilesetSpec, show: boolean): Promise<Cesium3DTileset | null> => {
			if (!viewer || viewer.isDestroyed()) return null;

			try {
				let tileset: Cesium3DTileset;

				if (spec.kind === 'ion') {
					tileset = await Cesium3DTileset.fromIonAssetId(spec.cesiumId, {
						maximumScreenSpaceError: 8,
						skipLevelOfDetail: true,
						baseScreenSpaceError: 1024,
						skipScreenSpaceErrorFactor: 16,
						skipLevels: 1,
						immediatelyLoadDesiredLevelOfDetail: false,
						loadSiblings: false,
						cullWithChildrenBounds: true,
						preloadWhenHidden: false,
					});

					(tileset as any).cesiumId = spec.cesiumId;
					(tileset as any).subSurface = spec.subSurface;

					const extras = (tileset.asset as any)?.extras;
					if (defined(extras?.ion?.defaultStyle)) {
						tileset.style = new Cesium3DTileStyle(extras.ion.defaultStyle);
					}
				} else {
					const url = `${spec.url}${spec.url.includes('?') ? '&' : '?'}v=${encodeURIComponent(spec.version)}`;
					tileset = await Cesium3DTileset.fromUrl(url, {
						maximumScreenSpaceError: 8,
					});
					(tileset as any).tilesetType = spec.tilesetType;
					(tileset as any).sidecarUrl = spec.tilesetType === 'bim' ? sidecarUrlFor(url) : null;
				}

				(tileset as any).name = spec.name;
				(tileset as any).uniqueId = spec.uniqueId;
				(tileset as any).isLayer = true;
				(tileset as any).specSignature = specSignature(spec);
				(tileset as LayerTileset).isSplat = spec.kind === 'url' && spec.splat;

				if (viewer.isDestroyed()) return null;

				tileset.show = show;
				viewer.scene.primitives.add(tileset);

				// IFC models get their attribute index; everything the panel shows comes from it.
				if (spec.kind === 'url' && spec.tilesetType === 'bim') {
					registerIfcTileset({
						key: spec.uniqueId,
						name: spec.name,
						tileset,
						sidecarUrl: (tileset as any).sidecarUrl as string | null,
					});
				}

				return tileset;
			} catch (err) {
				console.error('Failed to load tileset:', spec, err);
				if (spec.kind === 'ion') {
					toast.error(translate('viewer.loader.tilesetFailedId', { id: spec.cesiumId }));
				} else {
					toast.error(translate('viewer.loader.tilesetFailedName', { name: spec.name ?? spec.uniqueId }));
				}
				return null;
			}
		},
		[viewer, registerIfcTileset],
	);

	useEffect(() => {
		if (!viewer || isLoading || isLoadingTilesets) return;

		const loadAssets = async () => {
			const primitives = viewer.scene.primitives;

			specs.current = new Map(
				[...tilesetsToLoad, ...urlTilesetsToLoad].map((spec) => [spec.uniqueId, spec])
			);

			// Keep the tilesets that are still wanted and unchanged. Only a layer that
			// disappeared or whose content actually moved gets torn down, so a Set Location
			// save no longer reloads every model on screen.
			const reused = new Map<string, Cesium3DTileset>();
			for (let i = primitives.length - 1; i >= 0; i--) {
				const prim = primitives.get(i) as any;
				if (prim?.isLayer !== true) continue;

				const spec = specs.current.get(prim.uniqueId);
				if (!spec || specSignature(spec) !== prim.specSignature) {
					if (prim.tilesetType === 'bim') unregisterIfcTileset(prim.uniqueId);
					primitives.remove(prim);
				} else {
					reused.set(prim.uniqueId, prim as Cesium3DTileset);
				}
			}

			try {
				const subSurfaceIds: string[] = [];
				const deferred: DeferredLayerInfo[] = [];
				const toCreate: TilesetSpec[] = [];

				for (const spec of specs.current.values()) {
					const isSubSurface = spec.kind === 'ion' && !!spec.subSurface;
					if (isSubSurface) subSurfaceIds.push(spec.uniqueId);

					const kept = reused.get(spec.uniqueId) as any;
					if (kept) {
						// Left in place; refresh only the metadata the layer panel reads.
						kept.name = spec.name;
						if (spec.kind === 'ion') kept.subSurface = spec.subSurface;
						continue;
					}

					// Switched off by the user: keep it listed, but don't fetch anything.
					// It is built on demand when the layer is switched back on.
					const savedVisibility = getLayerVisibility(spec.uniqueId, undefined);
					if (savedVisibility === false && spec.name) {
						deferred.push({
							id: spec.uniqueId,
							name: spec.name,
							cesiumId: spec.kind === 'ion' ? spec.cesiumId : undefined,
							subSurface: isSubSurface,
						});
						continue;
					}

					toCreate.push(spec);
				}

				// Sync the registries by difference so the layer panel doesn't flicker.
				const nextSubSurface = new Set(subSurfaceIds);
				registeredIds.current.forEach((id) => {
					if (!nextSubSurface.has(id)) {
						registerSubSurfaceLayer({ id, name: '', type: 'primitive' }, 'remove');
					}
				});
				subSurfaceIds.forEach((id) =>
					registerSubSurfaceLayer(
						{ id, name: specs.current.get(id)?.name ?? '', type: 'primitive' },
						'add'
					)
				);
				registeredIds.current = subSurfaceIds;

				const nextDeferred = new Set(deferred.map((info) => info.id));
				deferredIds.current.forEach((id) => {
					if (!nextDeferred.has(id)) registerDeferredLayer({ id, name: '' }, 'remove');
				});
				deferred.forEach((info) => registerDeferredLayer(info, 'add'));
				deferredIds.current = deferred.map((info) => info.id);

				if (specs.current.size === 0) {
					toast(translate('viewer.loader.noTiles'));
					loadedTilesets.current = [];
					onTilesetsLoadedRef.current?.([]);
					return;
				}

				const results = await Promise.allSettled(
					toCreate.map((spec) => {
						const isSubSurface = spec.kind === 'ion' && !!spec.subSurface;
						// Sub-surface layers stay hidden until underground mode is on; the
						// effect below flips them once it is.
						const show = isSubSurface
							? getLayerVisibility(spec.uniqueId, undefined) === true && undergroundEnabled
							: true;
						return instantiate(spec, show);
					})
				);

				const created = results
					.filter(
						(r): r is PromiseFulfilledResult<Cesium3DTileset> =>
							r.status === 'fulfilled' && r.value !== null
					)
					.map((r) => r.value);

				// A tileset that failed to build (network, bad URL, tiles gone from storage)
				// stays in the layer list as a switched-off entry, so it is not silently
				// lost and can be retried by switching it on. Its saved visibility is
				// turned off so the next load does not fail again unasked.
				toCreate.forEach((spec, index) => {
					const result = results[index];
					if (result.status === 'fulfilled' && result.value !== null) return;
					registerDeferredLayer(
						{
							id: spec.uniqueId,
							name: spec.name,
							cesiumId: spec.kind === 'ion' ? spec.cesiumId : undefined,
							subSurface: spec.kind === 'ion' && !!spec.subSurface,
						},
						'add'
					);
					deferredIds.current = [...deferredIds.current, spec.uniqueId];
					updateSetting('layerVisibility', { [spec.uniqueId]: false });
				});

				loadedTilesets.current = [...reused.values(), ...created];

				if (toCreate.length > 0 && created.length === 0) {
					console.warn('No valid tilesets could be loaded.');
				} else {
					console.info(
						`Tilesets: ${created.length} loaded, ${reused.size} reused, ${deferred.length} deferred.`
					);
				}

				onTilesetsLoadedRef.current?.(loadedTilesets.current);
			} catch (err) {
				toast.error(translate('viewer.loader.unexpectedError'));
				console.error('Failed to load 3D Tilesets:', err);
			} finally {
				setTilesLoaded(true);
			}
		};

		loadAssets();
	}, [
		viewer,
		tilesetsToLoad,
		urlTilesetsToLoad,
		isLoading,
		isLoadingTilesets,
		setTilesLoaded,
		registerSubSurfaceLayer,
		registerDeferredLayer,
		instantiate,
		unregisterIfcTileset,
	]);

	// Only on unmount — a re-run reconciles the registries itself.
	useEffect(() => {
		return () => {
			registeredIds.current.forEach((id) =>
				registerSubSurfaceLayer({ id, name: '', type: 'primitive' }, 'remove')
			);
			deferredIds.current.forEach((id) => registerDeferredLayer({ id, name: '' }, 'remove'));
		};
	}, [registerSubSurfaceLayer, registerDeferredLayer]);

	// A deferred layer was switched on: build it now.
	const loadDeferred = useCallback(
		async (id: string) => {
			const spec = specs.current.get(id);
			if (!spec) {
				resolveLayerLoad(id);
				return;
			}

			const tileset = await instantiate(spec, true);

			if (!tileset) {
				// Keep the entry listed (switched off) so the user can retry; the toast
				// from instantiate() already said why it failed.
				updateSetting('layerVisibility', { [id]: false });
				resolveLayerLoad(id);
				return;
			}

			registerDeferredLayer({ id, name: spec.name }, 'remove');
			deferredIds.current = deferredIds.current.filter((d) => d !== id);
			resolveLayerLoad(id);

			loadedTilesets.current = [...loadedTilesets.current, tileset];
			onTilesetsLoadedRef.current?.(loadedTilesets.current);
			if (!viewer?.isDestroyed()) viewer?.scene.requestRender();
		},
		[viewer, instantiate, registerDeferredLayer, resolveLayerLoad, updateSetting]
	);

	useEffect(() => {
		if (!viewer || pendingLayerLoads.length === 0) return;

		pendingLayerLoads.forEach((id) => {
			if (inFlight.current.has(id)) return;
			inFlight.current.add(id);
			void loadDeferred(id).finally(() => inFlight.current.delete(id));
		});
	}, [viewer, pendingLayerLoads, loadDeferred]);

	useEffect(() => {
		if (!viewer) return;

		const primitives = viewer.scene.primitives;

		for (let i = 0; i < primitives.length; i++) {
			const prim = primitives.get(i);
			const uniqueId = (prim as any).uniqueId;

			if ((prim as any).subSurface === true) {
				if (!undergroundEnabled) {
					prim.show = false;
				} else {
					const userPref = getLayerVisibility(uniqueId, true);
					prim.show = userPref;
				}
			}
		}

		viewer.scene.requestRender();
	}, [viewer, undergroundEnabled]);

	return null;
};

export default TilesLoader;
